import type { UseFetchOptions, UseFetchReturn } from "@vueuse/core";
import { computed, type MaybeRef, type MaybeRefOrGetter, toValue } from "vue";

import { useBookmarksStore } from "@/stores/bookmarks.ts";
import type { BookmarkResponse } from "@/types/content.d.ts";
import { bookmarkApiClient } from "@/utils/apiClients.ts";

const useBookmarksByPage = (
  payload: MaybeRef<{
    pageNumber: number;
    pageSize: number;
    order: "NEWEST_FIRST" | "OLDEST_FIRST";
  }>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<BookmarkResponse> => {
  return bookmarkApiClient("bookmarks/page", options)
    .post(() => toValue(payload))
    .json();
};

const useBookmarkStatus = (
  urn: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<boolean> => {
  const url = computed(() => `bookmarks/${toValue(urn)}/status`);
  return bookmarkApiClient(url, options).json();
};

const useBookmarksStatus = (
  options: UseFetchOptions = { immediate: false, initialData: {} },
): UseFetchReturn<Record<string, boolean>> => {
  return bookmarkApiClient("bookmarks/status", options).json();
};

const useAddAll = (
  payload: MaybeRefOrGetter<string[]>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<string> => {
  return bookmarkApiClient("bookmarks/addAll", options)
    .post(() => toValue(payload))
    .json();
};

const useAddItem = (
  urn: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<unknown> => {
  const bookmarkStore = useBookmarksStore();
  const url = computed(() => `bookmarks/${toValue(urn)}`);
  return bookmarkApiClient(url, {
    ...options,
    async afterFetch({ data }) {
      await bookmarkStore.handleBookmarkAdd();
      return { data };
    },
  }).post();
};

const useRemoveItem = (
  urn: MaybeRef<string>,
  options: UseFetchOptions = { immediate: false, refetch: false },
): UseFetchReturn<unknown> => {
  const bookmarkStore = useBookmarksStore();
  const url = computed(() => `bookmarks/${toValue(urn)}`);
  return bookmarkApiClient(url, {
    ...options,
    async afterFetch({ data }) {
      await bookmarkStore.handleBookmarkRemove(toValue(urn));
      return { data };
    },
  }).delete();
};

const useRemoveAllUnavailableItems = (
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<unknown> => {
  const bookmarkStore = useBookmarksStore();
  return bookmarkApiClient("bookmarks/deleteMissing", {
    ...options,
    afterFetch({ data }) {
      bookmarkStore.handleRemoveMissing();
      return { data };
    },
  }).post();
};

const useRemoveAllItems = (
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<unknown> => {
  const bookmarkStore = useBookmarksStore();
  return bookmarkApiClient("bookmarks/deleteAll", {
    ...options,
    afterFetch({ data }) {
      bookmarkStore.bookmarks = [];
      return { data };
    },
  }).post();
};

const useBookmarksCount = (
  options: UseFetchOptions = { immediate: false },
): UseFetchReturn<number> => {
  return bookmarkApiClient("bookmarks/count", options).json();
};

export {
  useAddAll,
  useAddItem,
  useBookmarksByPage,
  useBookmarksCount,
  useBookmarksStatus,
  useBookmarkStatus,
  useRemoveAllItems,
  useRemoveAllUnavailableItems,
  useRemoveItem,
};
